import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import InlineLoader from '../../../components/ui/InlineLoader'
import ViewSaleModal from '../../../components/modals/ViewSaleModal'
import AuthService from '../../../services/authService'
import BranchService from '../../../services/branchService'
import SalesService from '../../../services/salesService'
import { 
  RefreshCw, 
  Download,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  Users,
  AlertTriangle,
  Search,
  Eye,
  Calendar,
  ChevronDown,
  Printer,
  FileText,
  Globe,
  Filter,
  X
} from 'lucide-react'
import { SalesModel } from '../../../utils/salesModel'
import { EnhancedReceiptGenerator } from '../../../utils/enhancedReceiptGenerator'
import { ReceiptGenerator } from '../../../utils/receiptGenerator'
import { HTMLReceiptGenerator } from '../../../utils/htmlReceiptGenerator'
import { SimpleItemEnhancer } from '../../../utils/simpleItemEnhancer'
import { formatCurrency } from '../../../utils/formatters'

// Utility function to convert order types for API
const convertOrderTypeForAPI = (orderType) => {
  const typeMap = {
    'dine-in': 'dine_in',
    'takeaway': 'take_away',
    'delivery': 'delivery',
    'online': 'online'
  }
  return typeMap[orderType] || orderType
}

const Toast = ({ message, type, onClose }) => {
  const getToastStyles = () => {
    switch(type) {
      case 'success': return 'bg-green-500 text-white'
      case 'error': return 'bg-red-500 text-white'
      case 'info': return 'bg-blue-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  useEffect(() => {
    const timer = setTimeout(onClose, 5000)
    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div className={`fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${getToastStyles()}`}>
      <div className="flex items-center justify-between">
        <span>{message}</span>
        <button onClick={onClose} className="ml-4 text-white hover:text-gray-200">×</button>
      </div>
    </div>
  )
}

export const Sales = () => {
  const currentUser = AuthService.getCurrentUser()
  const userBranchId = currentUser?.branchId
  const isManager = currentUser?.role?.toLowerCase() === 'manager'
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin'

  // Use ref to track search timeout to prevent cursor jumping
  const searchTimeoutRef = useRef(null)

  const [sales, setSales] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [actionLoading, setActionLoading] = useState({})
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState(userBranchId || '')
  const [orderTypeFilter, setOrderTypeFilter] = useState('')
  const [dateFromFilter, setDateFromFilter] = useState('')

  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedSale, setSelectedSale] = useState(null)
  const [dropdownOpen, setDropdownOpen] = useState({}) // Track which dropdown is open

  const [stats, setStats] = useState({
    totalSales: 0,
    totalAmount: 0,
    avgOrderValue: 0,
    totalOrders: 0
  })

  const showToast = useCallback((message, type) => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }, [])

  const loadBranches = useCallback(async () => {
    try {
      const response = await BranchService.getAllBranches()
      let branchesData = []
      
      if (response.data?.branches) branchesData = response.data.branches
      else if (response.branches) branchesData = response.branches
      else if (Array.isArray(response.data)) branchesData = response.data
      else if (Array.isArray(response)) branchesData = response
      
      setBranches(Array.isArray(branchesData) ? branchesData : [])
    } catch (error) {
      console.error('Failed to load branches:', error)
      setBranches([])
    }
  }, [])

  // Use refs to store current filter values to avoid dependency issues
  const filtersRef = useRef({
    searchTerm: '',
    branchFilter: userBranchId || '',
    orderTypeFilter: '',
    dateFromFilter: ''
  })

  // Update refs whenever filters change
  useEffect(() => {
    filtersRef.current = {
      searchTerm,
      branchFilter,
      orderTypeFilter,
      dateFromFilter
    }
  }, [searchTerm, branchFilter, orderTypeFilter, dateFromFilter])

  const loadSales = useCallback(async (params = {}) => {
    try {
      const filterParams = { ...params }
      const currentFilters = filtersRef.current

      if (isManager && !isAdmin && userBranchId) {
        filterParams.branch = userBranchId
      } else if (currentFilters.branchFilter) {
        filterParams.branch = currentFilters.branchFilter
      }

      if (currentFilters.searchTerm) filterParams.search = currentFilters.searchTerm
      if (currentFilters.orderTypeFilter) filterParams.orderType = convertOrderTypeForAPI(currentFilters.orderTypeFilter)
      if (currentFilters.dateFromFilter) filterParams.dateFrom = currentFilters.dateFromFilter
      
      console.log('Loading sales with params:', filterParams); // Debug log
      
      const response = await SalesService.getAllSales(filterParams)
      const parsedData = SalesService.parseResponse(response)
      
      if (parsedData.sales) {
        const formattedSales = parsedData.sales.map(sale => SalesModel.formatSaleForDisplay(sale))
        console.log('Loaded sales data:', formattedSales.slice(0, 2)); // Debug: show first 2 sales
        setSales(formattedSales)
        
        // Calculate totals using the correct field name from SalesModel
        const totalAmount = formattedSales.reduce((sum, sale) => {
          const amount = sale.total || sale.total_amount || 0
          console.log(`Sale ${sale.orderNumber}: amount = ${amount}`) // Debug log
          return sum + amount
        }, 0)
        const totalOrders = formattedSales.length
        const avgOrderValue = totalOrders > 0 ? totalAmount / totalOrders : 0
        
        console.log(`💰 Stats calculated: ${totalOrders} orders, total: ${totalAmount}, avg: ${avgOrderValue}`) // Debug log
        
        setStats({
          totalSales: totalOrders,
          totalAmount,
          avgOrderValue,
          totalOrders
        })
      } else {
        setSales([])
        setStats({ totalSales: 0, totalAmount: 0, avgOrderValue: 0, totalOrders: 0 })
      }
      
      setError(null)
    } catch (err) {
      console.error('Error loading sales:', err)
      setError(err.message || 'Failed to load sales data')
      showToast('Failed to load sales data', 'error')
    }
  }, [userBranchId, isManager, isAdmin, showToast])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await loadSales()
      showToast('Sales data refreshed successfully', 'success')
    } catch (err) {
      console.error('Refresh error:', err)
      showToast('Failed to refresh sales data', 'error')
    } finally {
      setRefreshing(false)
    }
  }

  const handleViewSale = (sale) => {
    console.log('View sale clicked:', sale);
    setSelectedSale(sale)
    setShowViewModal(true)
  }

  // Download handlers for different formats
  const handleDownloadPDF = useCallback(async (sale) => {
    console.log('Download PDF clicked:', sale);
    console.log('Original sale items for PDF:', sale.items);
    const receiptId = `pdf-${sale.id || sale._id}`
    setActionLoading(prev => ({ ...prev, [receiptId]: true }))
    
    try {
      // First try simple enhancement with embedded data (faster)
      let enhancedItems = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items || [])
      console.log('Simple enhanced items for PDF:', enhancedItems);
      
      // Check if we still have unknown items that need API enhancement
      const hasUnknownItems = enhancedItems.some(item => 
        item.name.includes('Item #') || item.name === 'Unknown Item'
      )
      
      if (hasUnknownItems) {
        console.log('Some items still unknown, trying API enhancement for PDF...')
        enhancedItems = await EnhancedReceiptGenerator.enhanceItemsWithProductDetails(sale.items || [])
        console.log('API enhanced items for PDF:', enhancedItems);
      }
      
      const enhancedSale = { ...sale, items: enhancedItems }
      
      ReceiptGenerator.downloadReceipt(enhancedSale, `receipt_${sale.orderNumber || 'unknown'}.pdf`)
      showToast('PDF receipt downloaded successfully', 'success')
    } catch (err) {
      console.error('Error generating PDF:', err)
      showToast('Failed to generate PDF receipt', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [receiptId]: false }))
      setDropdownOpen(prev => ({ ...prev, [sale.id || sale._id]: false }))
    }
  }, [showToast])

  const handleDownloadHTML = useCallback(async (sale) => {
    console.log('Download HTML clicked:', sale);
    console.log('Original sale items:', sale.items);
    const receiptId = `html-${sale.id || sale._id}`
    setActionLoading(prev => ({ ...prev, [receiptId]: true }))
    
    try {
      // First try simple enhancement with embedded data (faster)
      let enhancedItems = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items || [])
      console.log('Simple enhanced items:', enhancedItems);
      
      // Check if we still have unknown items that need API enhancement
      const hasUnknownItems = enhancedItems.some(item => 
        item.name.includes('Item #') || item.name === 'Unknown Item'
      )
      
      if (hasUnknownItems) {
        console.log('Some items still unknown, trying API enhancement...')
        enhancedItems = await EnhancedReceiptGenerator.enhanceItemsWithProductDetails(sale.items || [])
        console.log('API enhanced items:', enhancedItems);
      }
      
      const enhancedSale = { ...sale, items: enhancedItems }
      
      HTMLReceiptGenerator.downloadHTML(enhancedSale, `receipt_${sale.orderNumber || 'unknown'}.html`)
      showToast('HTML receipt downloaded successfully', 'success')
    } catch (err) {
      console.error('Error generating HTML:', err)
      showToast('Failed to generate HTML receipt', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [receiptId]: false }))
      setDropdownOpen(prev => ({ ...prev, [sale.id || sale._id]: false }))
    }
  }, [showToast])

  const handlePrintReceipt = useCallback(async (sale) => {
    console.log('Print receipt clicked:', sale);
    const receiptId = `print-${sale.id || sale._id}`
    setActionLoading(prev => ({ ...prev, [receiptId]: true }))
    
    try {
      // First try simple enhancement with embedded data (faster)
      let enhancedItems = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items || [])
      
      // Check if we still have unknown items that need API enhancement
      const hasUnknownItems = enhancedItems.some(item => 
        item.name.includes('Item #') || item.name === 'Unknown Item'
      )
      
      if (hasUnknownItems) {
        console.log('Some items still unknown, trying API enhancement...')
        enhancedItems = await EnhancedReceiptGenerator.enhanceItemsWithProductDetails(sale.items || [])
      }
      
      const enhancedSale = { ...sale, items: enhancedItems }
      
      HTMLReceiptGenerator.printReceipt(enhancedSale)
      showToast('Print dialog opened', 'success')
    } catch (err) {
      console.error('Error opening print dialog:', err)
      showToast('Failed to open print dialog', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [receiptId]: false }))
      setDropdownOpen(prev => ({ ...prev, [sale.id || sale._id]: false }))
    }
  }, [showToast])

  const toggleDropdown = (saleId) => {
    setDropdownOpen(prev => ({ ...prev, [saleId]: !prev[saleId] }))
  }

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchTerm || branchFilter || orderTypeFilter || dateFromFilter
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setBranchFilter(userBranchId || '')
    setOrderTypeFilter('')
    setDateFromFilter('')
  }

  useEffect(() => {
    const initialize = async () => {
      setLoading(true)
      try {
        await Promise.all([loadBranches(), loadSales()]) // Simplified call
      } catch (err) {
        console.error('Error initializing sales page:', err)
        setError('Failed to initialize sales data')
      } finally {
        setLoading(false)
      }
    }
    initialize()
  }, [loadBranches, loadSales])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-menu')) {
        setDropdownOpen({})
      }
    }
    
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Stable search input handler
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value
    setSearchTerm(value)
  }, [])

  // Simple search effect with debouncing
  useEffect(() => {
    if (!loading) {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      
      // Set new timeout
      searchTimeoutRef.current = setTimeout(() => {
        loadSales() // Use the existing loadSales function
      }, 300)
      
      // Cleanup function
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current)
        }
      }
    }
  }, [searchTerm, branchFilter, orderTypeFilter, dateFromFilter, loading, loadSales])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <InlineLoader />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Management</h1>
        </div>
        <Button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-100">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-purple-100">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Average Order</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.avgOrderValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-orange-100">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSales}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filter Units</h3>
            </div>
            {hasActiveFilters() && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 px-4 py-2 text-orange-500 border border-orange-300 rounded-lg hover:bg-orange-50 transition-colors"
              >
                
                <span>Clear Filters</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  key="search-input"
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <select
                  value={orderTypeFilter}
                  onChange={(e) => setOrderTypeFilter(e.target.value)}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-gray-50 text-gray-700"
                >
                  <option value="">All Types</option>
                  <option value="dine-in">Dine In</option>
                  <option value="takeaway">Takeaway</option>
                  <option value="delivery">Delivery</option>
                  <option value="online">Online</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              </div>
            </div>

            {(isAdmin || (!isManager && !userBranchId)) && (
              <div>
                <div className="relative">
                  <select
                    value={branchFilter}
                    onChange={(e) => setBranchFilter(e.target.value)}
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-gray-50 text-gray-700"
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch.id || branch._id} value={branch.id || branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                </div>
              </div>
            )}

            <div>
              <input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              />
            </div>
          </div>

          {sales.length > 0 && (
            <div className="mt-4 text-sm text-gray-600">
              Showing {sales.length} of {sales.length} units
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="flex items-center justify-center py-12 text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          ) : sales.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {hasActiveFilters() ? 'No sales match your filters' : 'No sales found'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {hasActiveFilters() 
                  ? 'Try adjusting your search criteria or clearing filters.'
                  : 'No completed orders found matching your criteria.'
                }
              </p>
              {hasActiveFilters() && (
                <button
                  onClick={clearFilters}
                  className="mt-3 inline-flex items-center px-3 py-2 text-sm text-orange-600 hover:text-orange-800"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sales.map((sale) => (
                    <tr key={sale.id || sale._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{sale.orderNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {sale.branchName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {sale.customerName}
                          </div>
                          {sale.customerMobile && (
                            <div className="text-sm text-gray-500">
                              {sale.customerMobile}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sale.orderTypeConfig?.badgeClass || 'bg-gray-100 text-gray-800'}`}>
                          {sale.orderTypeConfig?.label || sale.orderType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {sale.itemsCount} items
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {sale.discount > 0 ? (
                            <span className="text-green-600 font-medium">
                              - {sale.discountFormatted || formatCurrency(sale.discount || 0)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {sale.totalFormatted || formatCurrency(sale.total || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sale.paymentStatusConfig?.badgeClass || 'bg-green-100 text-green-800'}`}>
                          {sale.paymentStatusConfig?.label || sale.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm text-gray-900">
                              {sale.orderDateFormatted || new Date(sale.created_at).toLocaleDateString()}
                            </div>
                            <div className="text-sm text-gray-500">
                              {sale.orderTimeFormatted || new Date(sale.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleViewSale(sale)}
                            className="p-2 text-gray-400 hover:text-gray-500 "
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {/* Download Dropdown */}
                          <div className="relative dropdown-menu">
                            <button
                              onClick={() => toggleDropdown(sale.id || sale._id)}
                              className="p-2 text-green-500 hover:text-green-700  flex items-center"
                              title="Download Options"
                            >
                              <Download className="h-4 w-4" />
                              <ChevronDown className="h-3 w-3 ml-1" />
                            </button>
                            
                            {dropdownOpen[sale.id || sale._id] && (
                              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                <div className="py-1">
                                  <button
                                    onClick={() => handleDownloadPDF(sale)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    disabled={actionLoading[`pdf-${sale.id || sale._id}`]}
                                  >
                                    {actionLoading[`pdf-${sale.id || sale._id}`] ? (
                                      <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full mr-3"></div>
                                    ) : (
                                      <FileText className="h-4 w-4 text-red-500 mr-3" />
                                    )}
                                    Download PDF
                                  </button>
                                  
                                  <button
                                    onClick={() => handleDownloadHTML(sale)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    disabled={actionLoading[`html-${sale.id || sale._id}`]}
                                  >
                                    {actionLoading[`html-${sale.id || sale._id}`] ? (
                                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
                                    ) : (
                                      <Globe className="h-4 w-4 text-blue-500 mr-3" />
                                    )}
                                    Download HTML
                                  </button>
                                  
                                  <button
                                    onClick={() => handlePrintReceipt(sale)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    disabled={actionLoading[`print-${sale.id || sale._id}`]}
                                  >
                                    {actionLoading[`print-${sale.id || sale._id}`] ? (
                                      <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full mr-3"></div>
                                    ) : (
                                      <Printer className="h-4 w-4 text-green-500 mr-3" />
                                    )}
                                    Print Receipt
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showViewModal && selectedSale && (
        <ViewSaleModal
          isOpen={showViewModal}
          sale={selectedSale}
          onClose={() => {
            setShowViewModal(false)
            setSelectedSale(null)
          }}
        />
      )}
    </div>
  )
}

export default Sales
